import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../static/pets-records.css";

const HOURS = Array.from({ length: 13 }, (_, index) => {
  const hour = index + 8;
  return `${String(hour).padStart(2, "0")}:00`;
});

const todayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const safeArray = (value) => (Array.isArray(value) ? value : []);
const entityId = (value) => {
  if (value && typeof value === "object") return value.id || "";
  return value || "";
};

const newestFirst = (items, dateField, timeField) =>
  [...safeArray(items)].sort((a, b) => {
    const aValue = `${a[dateField] || ""}T${a[timeField] || "00:00"}`;
    const bValue = `${b[dateField] || ""}T${b[timeField] || "00:00"}`;

    return bValue.localeCompare(aValue);
  });

const groomingTaskLabel = (task) => {
  const labels = {
    1: "Wash & Go",
    2: "Full Grooming",
    3: "Nail Care",
    4: "Teeth",
    5: "Other",
  };

  return labels[task] || "Grooming";
};
 


const ownerIdOf = (owner) => {
  if (owner && typeof owner === "object") return owner.id || "";
  return owner || "";
};

const formatDate = (value) => {
  if (!value) return "—";
  return String(value).slice(0, 10);
};

const AddRecordForm = ({
  type,
  owner,
  pets,
  onSaved,
  onClose,
}) => {
  const [petId, setPetId] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    startDate: todayKey(),
    endDate: todayKey(),
    startHour: "",
    endHour: "",
    price: "0.00",
    note: "",
    teethType: "1",
    session: "1",
  });

  useEffect(() => {
    if (!petId && pets.length) {
      setPetId(String(pets[0].id));
    }
  }, [pets, petId]);

  const selectedPet = pets.find((pet) => String(pet.id) === String(petId));

  const ownerId = ownerIdOf(owner);
  const ownerName = owner?.name || "";
  const ownerPhone =
    owner?.phone_mobile ||
    owner?.phone ||
    selectedPet?.phone ||
    "";

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setMessage("");

    if (!ownerId || !selectedPet) {
      setMessage("Please select an owner and pet.");
      return;
    }

    if (!ownerPhone) {
      setMessage("Owner phone number is missing.");
      return;
    }

    if (!form.startDate || !form.startHour) {
      setMessage("Please choose a date and hour.");
      return;
    }

    setSaving(true);

    let endpoint = "";
    let payload = {};

    if (type === "quick") {
      endpoint = "/petgrooming/quick-appointments/";

      payload = {
        pet_name: selectedPet.name || "",
        catdog: selectedPet.catdog === "Cat" ? "Cat" : "Dog",
        owner_name: ownerName,
        owner_phone: ownerPhone,
        appointment_date: form.startDate,
        appointment_hour: form.startHour,
        note:
          form.note ||
          "Quick booking — full details to be completed later.",
      };
    }

    if (type === "daycare") {
      endpoint = "/create_day_care/";

      payload = {
        owner: ownerId,
        pet: selectedPet.id,
        phone: ownerPhone,
        fromday: form.startDate,
        endday: form.endDate,
        days: form.startDate,
        daye: form.endDate,
        times: form.startHour,
        timee: form.endHour,
        price: form.price || "0.00",
        note: form.note || "",
        done: 0,
      };
    }

    if (type === "walking") {
      endpoint = "/create_cat_walking/";

      payload = {
        owner: ownerId,
        pet: selectedPet.id,
        phone: ownerPhone,
        days: form.startDate,
        daye: form.endDate,
        times: form.startHour,
        timee: form.endHour,
        price: form.price || "0.00",
        note: form.note || "",
        done: 0,
      };
    }

    if (type === "teeth") {
      const ultrasound = form.teethType === "3";

      endpoint = ultrasound
        ? "/create_teeth_ultrasound/"
        : "/create_teeth/";

      payload = {
        owner: ownerId,
        pet: selectedPet.id,
        phone: ownerPhone,
        session: Number(form.session || 1),
        day: form.startDate,
        hour: form.startHour,
        nextsession: form.endDate,
        nextsessionhour: form.endHour,
        note: form.note || "",
        done: 0,
      };

      if (!ultrasound) {
        payload.type = Number(form.teethType);
      }
    }

    try {
      const response = await axios.post(endpoint, payload);

      onSaved(type, {
        ...payload,
        ...(response.data || {}),
        id: response.data?.id || `local-${Date.now()}`,
      });

      onClose();
    } catch (error) {
      console.error(error.response?.data || error);

      setMessage(
        error.response?.data
          ? JSON.stringify(error.response.data)
          : "Could not save this record."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="record-form" onSubmit={submit}>
      <div className="record-form-grid">
        <label>
          Pet
          <select value={petId} onChange={(e) => setPetId(e.target.value)}>
            {pets.map((pet) => (
              <option key={pet.id} value={pet.id}>
                {pet.name} · {pet.catdog || "Pet"}
              </option>
            ))}
          </select>
        </label>

        <label>
          Owner
          <input value={`${ownerName} · ${ownerPhone}`} readOnly />
        </label>

        {type === "teeth" && (
          <>
            <label>
              Teeth type
              <select
                name="teethType"
                value={form.teethType}
                onChange={update}
              >
                <option value="1">Brushing</option>
                <option value="2">Descaling</option>
                <option value="3">Ultrasound</option>
              </select>
            </label>

            <label>
              Session number
              <input
                type="number"
                min="1"
                name="session"
                value={form.session}
                onChange={update}
              />
            </label>
          </>
        )}

        <label>
          Start date
          <input
            type="date"
            name="startDate"
            value={form.startDate}
            onChange={update}
            required
          />
        </label>

        <label>
          Start hour
          <select
            name="startHour"
            value={form.startHour}
            onChange={update}
            required
          >
            <option value="">Choose hour</option>
            {HOURS.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
        </label>

        {type !== "quick" && (
          <>
            <label>
              End / next date
              <input
                type="date"
                name="endDate"
                value={form.endDate}
                onChange={update}
                required
              />
            </label>

            <label>
              End / next hour
              <select
                name="endHour"
                value={form.endHour}
                onChange={update}
                required
              >
                <option value="">Choose hour</option>
                {HOURS.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {(type === "daycare" || type === "walking") && (
          <label>
            Price (£)
            <input
              type="number"
              step="0.01"
              min="0"
              name="price"
              value={form.price}
              onChange={update}
            />
          </label>
        )}

        <label className="record-form-full">
          Note
          <textarea
            rows="3"
            name="note"
            value={form.note}
            onChange={update}
            placeholder="Optional note"
          />
        </label>
      </div>

      {message && <div className="record-error">{message}</div>}

      <div className="record-form-actions">
        <button type="button" onClick={onClose}>
          Cancel
        </button>

        <button className="primary" type="submit" disabled={saving}>
          {saving ? "Saving..." : "Save record"}
        </button>
      </div>
    </form>
  );
};

const RecordSection = ({
  title,
  description,
  count,
  type,
  openForm,
  setOpenForm,
  owner,
  pets,
  onSaved,
  children,
}) => {
  const canAdd = Boolean(ownerIdOf(owner) && pets.length);

  return (
    <section className="records-section">
      <div className="records-section-head">
        <div>
          <span>{title}</span>
          <p>{description}</p>
        </div>

        <div className="records-section-actions">
          <strong>{count}</strong>

          <button
            type="button"
            disabled={!canAdd}
            onClick={() =>
              setOpenForm(openForm === type ? "" : type)
            }
          >
            {openForm === type ? "Close" : "+ Add"}
          </button>
        </div>
      </div>

      {!canAdd && (
        <div className="records-tip">
          Search and select an owner first.
        </div>
      )}

      {openForm === type && (
        <AddRecordForm
          type={type}
          owner={owner}
          pets={pets}
          onSaved={onSaved}
          onClose={() => setOpenForm("")}
        />
      )}

      {children}
    </section>
  );
};

const Pets = () => {
  const navigate = useNavigate();
const [selectedPetId, setSelectedPetId] = useState("");
const [petMessages, setPetMessages] = useState([]);

const [petCommentForm, setPetCommentForm] = useState({
  subject: "",
  text: "",
  sender: "groomer",
});

const [savingPetComment, setSavingPetComment] = useState(false);
  const [phoneQuery, setPhoneQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);

  const [ownerData, setOwnerData] = useState(null);
  const [pets, setPets] = useState([]);
  const [groomingRecords, setGroomingRecords] = useState([]);
  const [daycareRecords, setDaycareRecords] = useState([]);
  const [walkingRecords, setWalkingRecords] = useState([]);
  const [teethRecords, setTeethRecords] = useState([]);
  const [quickRecords, setQuickRecords] = useState([]);

  const [openForm, setOpenForm] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageMessage, setPageMessage] = useState("");

  const ownerProfile = ownerData || selectedOwner || {};
  const ownerPhone =
    ownerProfile?.phone_mobile ||
    selectedOwner?.phone_mobile ||
    "";

  const loadOwner = async (owner = selectedOwner) => {
    if (!owner?.phone_mobile) return;

    setLoading(true);
    setPageMessage("");

    try {
      const [ownerResponse, quickResponse] = await Promise.all([
        axios.get(
          `/searchpet?phone=${encodeURIComponent(owner.phone_mobile)}`
        ),
        axios.get("/petgrooming/quick-appointments/"),
      ]);

      const data = ownerResponse.data || {};
      const ownerObject = data.owner || owner;

      setOwnerData(ownerObject);
     const loadedPets = safeArray(data.pets);

      setPets(loadedPets);

      setSelectedPetId(
        loadedPets.length > 0 ? String(loadedPets[0].id) : ""
      );
      setGroomingRecords(safeArray(data.grooming));
      setDaycareRecords(safeArray(data.daycare));
      setWalkingRecords(safeArray(data.pet_walking));

      const normalTeeth = safeArray(data.teeth);
      const ultrasoundTeeth = safeArray(data.teeth_ultrasound).map(
        (item) => ({
          ...item,
          type: 3,
        })
      );

      setTeethRecords([...normalTeeth, ...ultrasoundTeeth]);

      const matchingQuick = safeArray(quickResponse.data).filter(
        (item) =>
          item.status === "new" &&
          (item.owner_phone === owner.phone_mobile ||
            item.phone === owner.phone_mobile)
      );

      setQuickRecords(matchingQuick);
    } catch (error) {
      console.error(error);
      setPageMessage("Could not load this owner.");
    } finally {
      setLoading(false);
    }
  };

  const searchSuggestions = async (value) => {
    if (value.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `/autocomplete_owner_phone/?query=${encodeURIComponent(value)}`
      );

      setSuggestions(safeArray(response.data?.suggestions));
    } catch (error) {
      console.error(error);
    }
  };


useEffect(() => {
  const activeOwnerId = ownerIdOf(ownerProfile);

  if (!activeOwnerId || !selectedPetId) {
    setPetMessages([]);
    return;
  }

  axios
    .get(
      `/message-pet/?owner=${encodeURIComponent(
        activeOwnerId
      )}&pet=${encodeURIComponent(selectedPetId)}`
    )
    .then((response) => {
      const result = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setPetMessages(result);
    })
    .catch((error) => {
      console.error("Could not load pet messages:", error);
      setPetMessages([]);
    });
}, [ownerData, selectedOwner, selectedPetId]);

  const handlePhoneChange = (event) => {
    const value = event.target.value;
    setPhoneQuery(value);
    searchSuggestions(value);
  };

  const handleOwnerSelect = (owner) => {
    setSelectedOwner(owner);
    setPhoneQuery(owner.phone_mobile || "");
    setSuggestions([]);
  };

  const handleSearch = (event) => {
    event.preventDefault();

    if (!selectedOwner) {
      setPageMessage("Choose an owner from the suggestion list first.");
      return;
    }

    loadOwner(selectedOwner);
  };

  const handleRecordSaved = (type, record) => {
    if (type === "quick") {
      setQuickRecords((current) => [record, ...current]);
      setPageMessage("Quick booking saved.");
    }

    if (type === "daycare") {
      setDaycareRecords((current) => [record, ...current]);
      setPageMessage("Day care record saved.");
    }

    if (type === "walking") {
      setWalkingRecords((current) => [record, ...current]);
      setPageMessage("Pet walking record saved.");
    }

    if (type === "teeth") {
      setTeethRecords((current) => [record, ...current]);
      setPageMessage("Teeth record saved.");
    }
  };

  const allPets = useMemo(() => safeArray(pets), [pets]);
const selectedPet = allPets.find(
  (pet) => String(pet.id) === String(selectedPetId)
);

const selectedPetGrooming = useMemo(() => {
  return newestFirst(
    groomingRecords.filter(
      (record) =>
        String(entityId(record.pet)) === String(selectedPetId)
    ),
    "datee",
    "hour"
  );
}, [groomingRecords, selectedPetId]);

const sortedPetMessages = useMemo(() => {
  return newestFirst(petMessages, "datee", "time");
}, [petMessages]);

const latestPetGrooming = selectedPetGrooming[0] || null;


  const openPet = (pet) => {
    const query = new URLSearchParams({
      id: pet.id || "",
      name: pet.name || "",
      catdog: pet.catdog || "",
      owner: pet.owner?.id || pet.owner || ownerProfile.id || "",
      image: pet.image || "",
      phone: pet.phone || ownerPhone,
      breed: pet.breed || "",
      age: pet.age || "",
      behaviour: pet.behaviour || "",
      vaccinated: String(pet.vaccinated || false),
      neutered: String(pet.neutered || false),
    }).toString();

    navigate(`/historygroom/?${query}`);
  };



  const handlePetCommentSubmit = async (event) => {
  event.preventDefault();

  const activeOwnerId = ownerIdOf(ownerProfile);

  if (!selectedPet || !activeOwnerId) {
    setPageMessage("Select an owner and pet first.");
    return;
  }

  if (!petCommentForm.text.trim()) {
    setPageMessage("Write a comment before saving.");
    return;
  }

  setSavingPetComment(true);

  const now = new Date();

  const payload = {
    pet: selectedPet.id,
    owner: activeOwnerId,
    subject: petCommentForm.subject.trim() || "Pet comment",
    text: petCommentForm.text.trim(),
    sender: petCommentForm.sender,
    datee: todayKey(),
    time: now.toTimeString().slice(0, 5),
  };

  try {
    const response = await axios.post("/create-message/", payload);

    const savedComment = response.data?.id
      ? response.data
      : {
          ...payload,
          id: `local-message-${Date.now()}`,
        };

    setPetMessages((current) => [savedComment, ...current]);

    setPetCommentForm({
      subject: "",
      text: "",
      sender: "groomer",
    });

    setPageMessage(`Comment saved for ${selectedPet.name}.`);
  } catch (error) {
    console.error(error.response?.data || error);
    setPageMessage("Could not save the pet comment.");
  } finally {
    setSavingPetComment(false);
  }
};



  return (
    <main className="pets-records-page">
      <header className="pets-records-hero">
        <div>
          <span>Arys Grooming</span>
          <h1>Pet care records</h1>
          <p>
            Search an owner, then add grooming, day care, walking and teeth records.
          </p>
        </div>

        <form className="owner-search" onSubmit={handleSearch}>
          <label>
            Owner phone
            <input
              value={phoneQuery}
              onChange={handlePhoneChange}
              placeholder="Type phone number..."
              autoComplete="off"
            />
          </label>

          <button type="submit">Load records</button>

          {suggestions.length > 0 && (
            <div className="owner-suggestions">
              {suggestions.map((owner) => (
                <button
                  type="button"
                  key={owner.id}
                  onClick={() => handleOwnerSelect(owner)}
                >
                  <strong>{owner.name}</strong>
                  <span>{owner.phone_mobile}</span>
                </button>
              ))}
            </div>
          )}
        </form>
      </header>

      {pageMessage && (
        <div className="pets-page-message">{pageMessage}</div>
      )}

      {loading && <div className="pets-loading">Loading records...</div>}

      <section className="owner-summary">
        <div>
          <span>Owner</span>
          <strong>{ownerProfile?.name || "No owner selected"}</strong>
        </div>

        <div>
          <span>Phone</span>
          <strong>{ownerPhone || "—"}</strong>
        </div>

        <div>
          <span>Pets</span>
          <strong>{allPets.length}</strong>
        </div>

        <div>
          <span>Quick bookings</span>
          <strong>{quickRecords.length}</strong>
        </div>
      </section>

      <section className="pets-grid">
        {allPets.length > 0 ? (
          allPets.map((pet) => (
            <article className="pet-choice-card" key={pet.id}>
              <div>
                <span>{pet.catdog || "Pet"}</span>
                <h2>{pet.name}</h2>
                <p>{pet.breed || "Breed not added"}</p>
              </div>

              <div className="pet-choice-actions">
                <button
                  type="button"
                  className="manage-pet-button"
                  onClick={() => setSelectedPetId(String(pet.id))}
                >
                  Comments & history
                </button>

                <button type="button" onClick={() => openPet(pet)}>
                  Open profile
                </button>
              </div>
            </article>
          ))
        ) : (
          <div className="empty-owner-card">
            Search an owner to load their pets and care records.
          </div>
        )}
      </section>

<section className="pet-comment-history-grid">
  <article className="pet-comment-card">
    <div className="pet-record-card-head">
      <div>
        <span>Pet notes</span>
        <h2>Comments & messages</h2>
      </div>
    </div>

    {allPets.length > 0 ? (
      <>
        <label className="selected-pet-label">
          Choose pet
          <select
            value={selectedPetId}
            onChange={(event) => setSelectedPetId(event.target.value)}
          >
            {allPets.map((pet) => (
              <option key={pet.id} value={pet.id}>
                {pet.name} · {pet.catdog || "Pet"}
              </option>
            ))}
          </select>
        </label>

        <form
          className="pet-comment-form"
          onSubmit={handlePetCommentSubmit}
        >
          <input
            value={petCommentForm.subject}
            onChange={(event) =>
              setPetCommentForm((current) => ({
                ...current,
                subject: event.target.value,
              }))
            }
            placeholder="Short title, for example: Behaviour note"
          />

          <select
            value={petCommentForm.sender}
            onChange={(event) =>
              setPetCommentForm((current) => ({
                ...current,
                sender: event.target.value,
              }))
            }
          >
            <option value="groomer">Arys Grooming</option>
            <option value="owner">Owner</option>
          </select>

          <textarea
            rows="4"
            value={petCommentForm.text}
            onChange={(event) =>
              setPetCommentForm((current) => ({
                ...current,
                text: event.target.value,
              }))
            }
            placeholder={`Write a note about ${
              selectedPet?.name || "this pet"
            }...`}
            required
          />

          <button type="submit" disabled={savingPetComment}>
            {savingPetComment ? "Saving..." : "Save pet comment"}
          </button>
        </form>

        <div className="pet-message-list">
          {sortedPetMessages.length > 0 ? (
            sortedPetMessages.map((message, index) => (
              <article
                className="pet-message-item"
                key={message.id || index}
              >
                <div>
                  <strong>{message.subject || "Pet comment"}</strong>
                  <span>
                    {formatDate(message.datee)} ·{" "}
                    {String(message.time || "").slice(0, 5)}
                  </span>
                </div>

                <p>{message.text || "—"}</p>

                <small>
                  {message.sender === "owner"
                    ? ownerProfile?.name || "Owner"
                    : "Arys Grooming"}
                </small>
              </article>
            ))
          ) : (
            <div className="pet-history-empty">
              No comments for this pet yet.
            </div>
          )}
        </div>
      </>
    ) : (
      <div className="pet-history-empty">
        Search an owner and choose a pet first.
      </div>
    )}
  </article>

  <article className="pet-comment-card">
    <div className="pet-record-card-head">
      <div>
        <span>Grooming record</span>
        <h2>Grooming history</h2>
      </div>

      <strong className="history-count">
        {selectedPetGrooming.length}
      </strong>
    </div>

    {selectedPet ? (
      <>
        {latestPetGrooming && (
          <div className="latest-grooming-card">
            <span>Latest grooming</span>
            <strong>
              {formatDate(latestPetGrooming.datee)} ·{" "}
              {latestPetGrooming.hour || "Time pending"}
            </strong>
            <p>
              {groomingTaskLabel(latestPetGrooming.task)} ·{" "}
              {latestPetGrooming.commentus ||
                latestPetGrooming.commenthim ||
                latestPetGrooming.comment ||
                "No grooming note"}
            </p>
          </div>
        )}

        <div className="pet-grooming-list">
          {selectedPetGrooming.length > 0 ? (
            selectedPetGrooming.map((record, index) => (
              <article
                className="pet-grooming-item"
                key={record.id || index}
              >
                <strong>
                  {formatDate(record.datee)} · {record.hour || "—"}
                </strong>

                <span>{groomingTaskLabel(record.task)}</span>

                <p>
                  {record.commentus ||
                    record.commenthim ||
                    record.comment ||
                    "No grooming note"}
                </p>
              </article>
            ))
          ) : (
            <div className="pet-history-empty">
              No grooming history for {selectedPet.name} yet.
            </div>
          )}
        </div>
      </>
    ) : (
      <div className="pet-history-empty">
        Choose a pet to see grooming history.
      </div>
    )}
  </article>
</section>


      <RecordSection
        title="New grooming session"
        description="Quick booking. Full pet and owner details can be completed later."
        count={quickRecords.length}
        type="quick"
        openForm={openForm}
        setOpenForm={setOpenForm}
        owner={ownerProfile}
        pets={allPets}
        onSaved={handleRecordSaved}
      >
        <div className="records-list">
          {quickRecords.length ? (
            quickRecords.map((item, index) => (
              <article className="record-row quick" key={item.id || index}>
                <strong>{item.pet_name || item.dog_name}</strong>
                <span>{item.appointment_date} · {item.appointment_hour}</span>
                <small>{item.owner_name || ownerProfile.name}</small>
              </article>
            ))
          ) : (
            <p>No quick bookings yet.</p>
          )}
        </div>
      </RecordSection>

      <RecordSection
        title="Day care records"
        description="Add drop-off, pick-up, price and notes for any pet."
        count={daycareRecords.length}
        type="daycare"
        openForm={openForm}
        setOpenForm={setOpenForm}
        owner={ownerProfile}
        pets={allPets}
        onSaved={handleRecordSaved}
      >
        <div className="records-list">
          {daycareRecords.length ? (
            daycareRecords.map((item, index) => (
              <article className="record-row" key={item.id || index}>
                <strong>Pet #{item.pet}</strong>
                <span>{formatDate(item.fromday)} → {formatDate(item.endday)}</span>
                <small>{item.times} – {item.timee} · £{item.price || "0.00"}</small>
              </article>
            ))
          ) : (
            <p>No day care records yet.</p>
          )}
        </div>
      </RecordSection>

      <RecordSection
        title="Pet walking records"
        description="Add walking start/end times, dates, price and notes."
        count={walkingRecords.length}
        type="walking"
        openForm={openForm}
        setOpenForm={setOpenForm}
        owner={ownerProfile}
        pets={allPets}
        onSaved={handleRecordSaved}
      >
        <div className="records-list">
          {walkingRecords.length ? (
            walkingRecords.map((item, index) => (
              <article className="record-row" key={item.id || index}>
                <strong>Pet #{item.pet}</strong>
                <span>{formatDate(item.days)} → {formatDate(item.daye)}</span>
                <small>{item.times} – {item.timee} · £{item.price || "0.00"}</small>
              </article>
            ))
          ) : (
            <p>No pet walking records yet.</p>
          )}
        </div>
      </RecordSection>

      <RecordSection
        title="Teeth records"
        description="Add brushing, descaling or ultrasound sessions."
        count={teethRecords.length}
        type="teeth"
        openForm={openForm}
        setOpenForm={setOpenForm}
        owner={ownerProfile}
        pets={allPets}
        onSaved={handleRecordSaved}
      >
        <div className="records-list">
          {teethRecords.length ? (
            teethRecords.map((item, index) => (
              <article className="record-row" key={item.id || index}>
                <strong>
                  {item.type === 1
                    ? "Brushing"
                    : item.type === 2
                    ? "Descaling"
                    : "Ultrasound"}
                </strong>
                <span>{formatDate(item.day)} · {item.hour}</span>
                <small>Next: {formatDate(item.nextsession)} · {item.nextsessionhour}</small>
              </article>
            ))
          ) : (
            <p>No teeth records yet.</p>
          )}
        </div>
      </RecordSection>
    </main>
  );
};

export default Pets;
